/**
 * `useTenantStatusMutation` — TanStack Mutation for
 * PATCH /api/t/{slug}/candidates/{id}/tenant-status.
 *
 * Each call generates a fresh Idempotency-Key (UUID v4) via the API
 * client wrapper in `candidatesApi.ts` — one key per logical user action,
 * not per network retry.
 *
 * On success:
 *   - Invalidates `['candidate', tenantSlug, candidateId]` so the detail
 *     page refreshes the tenant_status field.
 *   - Invalidates `['candidate-pairings', tenantSlug, candidateId]` so
 *     the cascade summary is reflected.
 *   - Invalidates `['candidates', tenantSlug]` so the candidates table
 *     reflects the updated status.
 *   - Invalidates `['jd-candidates', tenantSlug]` so the dashboard candidate
 *     cards reflect the updated status across every JD this candidate is
 *     paired to.
 *
 * `refetchType: "all"` on the list invalidations is load-bearing
 * (fix/stale-candidate-card-after-delete, tenant-status arm). This mutation
 * is triggered from `<CandidatePageHeader>` / `<TenantStatusModal>` — never
 * from `<Dashboard>` or the candidates list. Those surfaces' list queries
 * (`["jd-candidates", …]`, `["candidates", …]`) are therefore INACTIVE when
 * the tenant-status change fires. With the default `refetchType: "active"`,
 * `invalidateQueries` marks them stale but never refetches them, so the
 * change is invisible until a fragile remount race resolves — same
 * inactive-query staleness root cause as the deleted-card bug. `"all"`
 * forces the inactive queries to refetch immediately.
 *
 * Spec: batch_2_5_design.md §Frontend + Invariants CASC-DEF-1.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import {
  patchTenantStatus,
  type TenantStatus,
  type CascadeMode,
  type TenantStatusPatchResponse,
} from "@/lib/candidatesApi";

interface TenantStatusVars {
  candidateId: string;
  tenantStatus: TenantStatus;
  cascade: CascadeMode;
}

export function useTenantStatusMutation(): UseMutationResult<
  TenantStatusPatchResponse,
  ApiError,
  TenantStatusVars
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();

  return useMutation<TenantStatusPatchResponse, ApiError, TenantStatusVars>({
    mutationFn: async (vars) => {
      return patchTenantStatus(
        tenantSlug!,
        vars.candidateId,
        vars.tenantStatus,
        vars.cascade,
      );
    },
    onSuccess: (_data, vars) => {
      // Invalidate single candidate + pairings + list.
      void queryClient.invalidateQueries({
        queryKey: ["candidate", tenantSlug, vars.candidateId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["candidate-pairings", tenantSlug, vars.candidateId],
      });
      // Candidates list — `refetchType: "all"` so the list refetches even
      // when `<Candidates>` is unmounted (this mutation runs from the
      // candidate page; inactive-query staleness — see header docstring).
      void queryClient.invalidateQueries({
        queryKey: ["candidates", tenantSlug],
        refetchType: "all",
      });
      // Dashboard candidate cards across every JD this candidate is paired to.
      // `refetchType: "all"` is mandatory — the dashboard is unmounted while
      // this mutation fires from the candidate page, so its
      // `["jd-candidates", …]` query is INACTIVE and the default `"active"`
      // would leave it stale. See the header docstring.
      void queryClient.invalidateQueries({
        queryKey: ["jd-candidates", tenantSlug],
        refetchType: "all",
      });
    },
  });
}
